import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ArrowRight } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

export default function TransferPage() {
  const [items, setItems] = useState([]);
  const [akun, setAkun] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { tanggal: todayISO(), dari_akun_id: "", ke_akun_id: "", jumlah: 0, biaya_admin: 0, keterangan: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [t, a] = await Promise.all([api.get("/transfer"), api.get("/akun", { params: { all: 1 } })]);
    setItems(t.data); setAkun(a.data);
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const openNew = () => { setEditId(null); setForm({ ...empty, tanggal: todayISO() }); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it, tanggal: toDateInput(it.tanggal) || todayISO(), keterangan: it.keterangan ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.dari_akun_id || !form.ke_akun_id) { toast.error("Pilih akun asal & tujuan"); return; }
    if (form.dari_akun_id === form.ke_akun_id) { toast.error("Akun asal & tujuan tidak boleh sama"); return; }
    if (!(Number(form.jumlah) > 0)) { toast.error("Jumlah transfer wajib > 0"); return; }
    const payload = { ...form, jumlah: Number(form.jumlah), biaya_admin: Number(form.biaya_admin) || 0 };
    try {
      if (editId) await api.put(`/transfer/${editId}`, payload);
      else await api.post("/transfer", payload);
      toast.success(editId ? "Diperbarui" : "Transfer tersimpan"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);
  const delOne = async (id) => { if (!confirm("Hapus transfer ini?")) return; await api.delete(`/transfer/${id}`); toast.success("Terhapus"); load(); };

  return (
    <>
      <PageHeader title="Transfer Antar Akun" subtitle="Pindah dana antar kas/bank. TIDAK dihitung sebagai pemasukan/pengeluaran (pajak tidak membengkak)." actions={
        <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-transfer-btn"><Plus size={14} className="mr-1" /> Tambah Transfer</Button>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="transfer-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Tanggal</th><th className="p-3">Dari</th><th className="p-3">Ke</th><th className="p-3 text-right">Jumlah</th><th className="p-3 text-right">Biaya Admin</th><th className="p-3 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Belum ada transfer.</td></tr>}
              {paged.map((p) => (

                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3 whitespace-nowrap">{tanggalID(p.tanggal)}</td>
                  <td className="p-3">{p.dari_nama}</td>
                  <td className="p-3"><span className="inline-flex items-center gap-1"><ArrowRight size={12} className="text-slate-400" />{p.ke_nama}</span></td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(p.jumlah)}</td>
                  <td className="p-3 text-right font-mono text-slate-500">{rupiah(p.biaya_admin)}</td>
                  <td className="p-3"><div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-transfer-${p.id}`}><Pencil size={14} /></button>
                    <button onClick={() => delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-transfer-${p.id}`}><Trash2 size={14} className="text-rose-600" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="transfer-pagination" />
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Transfer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} /></div>
            <div className="space-y-1.5"><Label>Jumlah (Rp) <span className="text-rose-500">*</span></Label><CurrencyInput value={form.jumlah} onValueChange={upd("jumlah")} /></div>
            <div className="space-y-1.5"><Label>Dari Akun <span className="text-rose-500">*</span></Label>
              <Select value={form.dari_akun_id} onValueChange={(v) => setForm((f) => ({ ...f, dari_akun_id: v }))}>
                <SelectTrigger data-testid="transfer-dari"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                <SelectContent>{akun.map((a) => <SelectItem key={a.id} value={a.id}>{a.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Ke Akun <span className="text-rose-500">*</span></Label>
              <Select value={form.ke_akun_id} onValueChange={(v) => setForm((f) => ({ ...f, ke_akun_id: v }))}>
                <SelectTrigger data-testid="transfer-ke"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                <SelectContent>{akun.map((a) => <SelectItem key={a.id} value={a.id}>{a.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Biaya Admin (Rp)</Label><CurrencyInput value={form.biaya_admin} onValueChange={upd("biaya_admin")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button><Button onClick={save} className="brand-btn rounded-sm" data-testid="transfer-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
